import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Chip, Stack, LinearProgress, Button, TextField, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import PageHeader from '../../components/common/PageHeader';
import StatCard from '../../components/common/StatCard';
import { loadSubscription, activateLicense } from './licensingSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const STATUS_COLOR = { ACTIVE: tokens.severity.low, TRIAL: tokens.severity.info, PAST_DUE: tokens.severity.medium, CANCELED: tokens.severity.critical };

export default function LicensingPage() {
  const dispatch = useDispatch();
  const { subscription, status, error } = useSelector((s) => s.licensing);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [entitlement, setEntitlement] = useState('');
  const [activateError, setActivateError] = useState(null);

  useEffect(() => { dispatch(loadSubscription()); }, [dispatch]);

  const handleActivate = async () => {
    if (!entitlement.trim()) return setActivateError('Paste the signed entitlement you received from Melsoft.');
    setActivateError(null);
    const result = await dispatch(activateLicense(entitlement.trim()));
    if (activateLicense.rejected.match(result)) {
      setActivateError(result.error?.message || 'Could not activate this entitlement — check it was copied in full.');
      return;
    }
    setDialogOpen(false);
    setEntitlement('');
  };

  if (!subscription) {
    return (
      <Box>
        <PageHeader title="Subscription & Licensing" subtitle="Plan, seat usage, and feature entitlements for the commercial platform" />
        <Paper sx={{ p: 2.4 }}>
          <Typography sx={{ color: status === 'failed' ? tokens.severity.critical : tokens.text.secondary }}>
            {status === 'failed' ? `Unable to load subscription: ${error || 'Request failed'}` : 'Loading subscription…'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  const licensed = Number(subscription.seatsLicensed) || 0;
  const used = Number(subscription.seatsUsed) || 0;
  const seatPct = licensed > 0 ? Math.min(100, Math.max(0, Math.round((used / licensed) * 100))) : 0;

  return (
    <Box>
      <PageHeader
        title="Subscription & Licensing"
        subtitle="Plan, seat usage, and feature entitlements for the commercial platform"
        action={<Button variant="contained" startIcon={<VpnKeyRoundedIcon />} onClick={() => setDialogOpen(true)}>Activate License</Button>}
      />
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={3}><StatCard label="Current Plan" value={subscription.plan} icon={WorkspacePremiumRoundedIcon} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Seats Used" value={`${subscription.seatsUsed} / ${subscription.seatsLicensed}`} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Status" value={subscription.status} accent={STATUS_COLOR[subscription.status]} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Renews" value={formatRelativeTime(subscription.renewsAt)} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Seat Usage</Typography>
            <LinearProgress
              variant="determinate" value={seatPct}
              sx={{ height: 8, borderRadius: 4, my: 1.5, backgroundColor: tokens.bg.elevated, '& .MuiLinearProgress-bar': { backgroundColor: seatPct > 90 ? tokens.severity.medium : tokens.brand.main } }}
            />
            <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted }}>{seatPct}% of licensed seats in use</Typography>

            <Typography sx={{ fontSize: '0.78rem', color: tokens.text.muted, mt: 3 }}>
              Paid plans can only be activated with a digitally signed entitlement issued by Melsoft.
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Included Features</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5, gap: 1 }}>
              {subscription.featureFlags.map((flag) => (
                <Chip key={flag} label={flag.replace(/_/g, ' ')} size="small" sx={{ color: tokens.text.secondary, backgroundColor: tokens.bg.elevated, border: `1px solid ${tokens.border}` }} />
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Activate License</DialogTitle>
        <DialogContent>
          {activateError && <Alert severity="error" sx={{ mb: 2 }}>{activateError}</Alert>}
          <TextField
            fullWidth multiline minRows={6} label="Signed Entitlement" value={entitlement}
            onChange={(e) => setEntitlement(e.target.value)} placeholder="Paste the signed entitlement issued by Melsoft…"
            sx={{ fontFamily: 'monospace', mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleActivate}>Activate</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
